import { flatten } from './functional/array.no-deps'

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

/**
 * Construct a type with the selected properties k from T and make them optional.
 */
export type OptionalPick<T, K extends keyof T> = Partial<Pick<T, K>>

export type Identifiable<T extends {}> = T & {
    id: number
}
export type OptionallyIdentifiable<T extends {}> = T & {
    id?: number
}

export type Nullable<T> = T | null
export type Nominal<type, name> = type & { __brand: name }
export type NominalUUID<name> = UUID & { __sub_brand?: name }
export type Replace<Source, By extends {}> = Omit<Source, keyof By> & By

export function tag<T, Brand>(value: T): Nominal<T, Brand> {
    return value as Nominal<T, Brand>
}

export function tagUUID<SubBrand>(uuid: UUID): NominalUUID<SubBrand> {
    return uuid as NominalUUID<SubBrand>
}

export type NeverKeys<T> = { [K in keyof T]: T[K] extends never ? K : never }[keyof T]
export type WithoutNever<T> = Omit<T, NeverKeys<T>>

// https://stackoverflow.com/a/57683652/2648987
export type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never
export type ExpandRecursively<T> = T extends object
    ? T extends infer O
        ? { [K in keyof O]: ExpandRecursively<O[K]> }
        : never
    : T

/**
 * Disallow attributes K to be set on type O.
 *
 * Example:
 *
 *    type Foo = DisallowAttributes<{foo: string}, "bar" | "baz">
 *
 *    function saveFoo(foo: Foo) { }
 *
 *    const foo = {foo: "Foo"}
 *    const foo2 = {foo: "Foo", bar: 1}
 *    const foo3 = {foo: "Foo", baz: 1}
 *
 *    saveFoo(foo) // no error
 *    saveFoo(foo2) // error
 *    saveFoo(foo3) // error
 **/
export type DisallowAttributes<O, K extends keyof any> = O & { [key in K]?: never }

export type Depromise<T> = T extends Promise<infer U> ? U : T

export type ReplaceStringLiterals<Source extends string, By extends { [key in Source]?: string }> =
    | Exclude<Source, keyof By>
    | By[keyof By]

export type Rename<T, From extends keyof T, To extends string> = Omit<T, From> & { [key in To]: T[From] }

/**
 *
 * Do NOT use anywhere other than an "UPDATE/INSERT/UPSERT" sql query!
 *
 * We are using JSON data types in the database. The custom SQL compiler needs to know the difference between
 * strings which are JSON and strings which are not to ensure that the types entered into the database are
 * correct.
 *
 * When attempting to insert an object into a JSON field, there are no problems. However, when attempting
 * to insert a string into a JSON field, the compiler does not recognise the string as JSON.
 *
 * Annotating the value as JSON ensures the compiler knows the correct type is being inserted.
 *
 */
export type json = string

/*
 * postgres returns dates as strings but it's nice to disambiguate
 */
export type DateString = string

/**
 * This type is used to describe the function passed a parameter to new Promise().
 * The original type cannot be accessed easily because it is declared inline as a constructor type.
 */
export type PromiseExecutor<T> = (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void

/**
 * This type just represents a callback
 */
export type Callback<T> = (error?: Error, result?: T) => void

// Define some types which are purely for the sanity of reading createQuery params
export type ActivityId = number
export type ActivityTemplateId = number
export type AuthorId = number
export type CampaignId = number
export type LocationId = number
export type KpiId = number
export type ReportId = number

export type ThenArg<T> = T extends Promise<infer U> ? U : T

export type Awaitable<T> = T | PromiseLike<T>

/**
 * Use this function to make sure that you have covered every variant of a given type.
 *
 * This function always returns `null`; this is useful for APIs that need to be backwards
 * compatible. If there are no circumstances under which you might have an unexpected
 * value, consider using `assertExhaustion` instead.
 */
export function assumeExhaustion(_: never): null {
    return null
}

/**
 * Use this function to make sure that you have covered every variant of a given type.
 *
 * This function always throws an error. The offending value will be appended to the
 * end of the second parameter (if provided) to form the error message. If you cannot
 * or should not guarantee there will never be an unexpected value, consider using
 * `assumeExhaustion` instead.
 */
export function assertExhaustion(x: never, reason = 'Unexpected object:'): never {
    throw new Error(reason + ' ' + x)
}

export type UUID = Nominal<string, 'UUID'>
export type Slug = string

export function isArray<T>(arrayOrNotArray: T | T[]): arrayOrNotArray is T[] {
    return Array.isArray(arrayOrNotArray)
}

export function extendedPromise<T, P extends Promise<T>>(
    ExtendedPromise: new (executor: PromiseExecutor<T>) => P,
    promise: Promise<T>
): P {
    return new ExtendedPromise((resolve, reject) => {
        promise.then(resolve, reject)
    })
}

export function createStringEnum<K extends string>(...strs: K[]) {
    return strs
}

/**
 * To be used temporarily with a plan to replace in place
 */
export const TEMPORARY_STUB: any = null

export type Tree<T> = Array<TreeNode<T>>
export type TreeNode<T> = { data: T; children: Tree<T> }

export type CustomerSlug = Nominal<string, 'CustomerSlug'>

export type Flavor<T, S extends string> = T & { __flavor: S }

export const findTreeNodeWithIterationPath = <T>(
    predicate: (data: T) => boolean,
    tree: Tree<T>,
    path: Array<TreeNode<T>>
): [Nullable<TreeNode<T>>, Array<TreeNode<T>>] => {
    return tree.reduce<[Nullable<TreeNode<T>>, Array<TreeNode<T>>]>(
        ([foundNode, _path], node) => {
            return foundNode !== null
                ? [foundNode, _path]
                : predicate(node.data)
                  ? [node, path]
                  : findTreeNodeWithIterationPath(predicate, node.children, [...path, node])
        },
        [null, path]
    )
}

export const treeValues = <T>(tree: Tree<T>): T[] => {
    return flatten(tree.map((node) => (node.children.length === 0 ? [node.data] : treeValues(node.children))))
}

export type ArraysOf<T> = T extends any ? T[] : never

export type FilterUnion<U, K extends keyof U, V extends U[K]> = U extends { [P in K]: V } ? U : never

// For use with Object.fromEntries
export type Entries<T> = Array<{ [K in keyof T]: [K, T[K]] }[keyof T]>

export type Primitive = string | number | boolean | Date | symbol | null | undefined

export type PrimitiveProperties<T> = {
    [K in keyof T]: T[K] extends Primitive ? K : never
}[keyof T]

export type CamelCase<S extends string> = S extends `${infer P1}-${infer P2}`
    ? CamelCase<`${P1}${Capitalize<P2>}`>
    : S extends `${infer P1}.${infer P2}`
      ? CamelCase<`${P1}${Capitalize<P2>}`>
      : S
